import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Mail, Lock, User, Building, Phone, MapPin, Loader2, AlertCircle, ArrowRight, CheckCircle } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const ROLES = [
  { value: 'Supplier', label: 'Supplier', desc: 'I want to connect with buyers' },
  { value: 'Buyer', label: 'Buyer', desc: 'I want to discover suppliers' },
  { value: 'Rep', label: 'Distributor Rep', desc: 'I want to earn incentives' }
];

export default function Register() {
  useSEO('register');
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    role: '',
    company: '',
    phone: '',
    state: ''
  });
  const [status, setStatus] = useState({ state: 'idle', msg: '' });

  const handleRoleSelect = (role) => {
    setFormData({ ...formData, role });
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setStatus({ state: 'error', msg: 'Passwords do not match' });
      return;
    }

    if (formData.password.length < 6) {
      setStatus({ state: 'error', msg: 'Password must be at least 6 characters' });
      return;
    }

    setStatus({ state: 'loading', msg: '' });

    try {
      const { confirmPassword, ...userData } = formData;
      await register(userData);
      navigate('/dashboard');
    } catch (err) {
      setStatus({ state: 'error', msg: err.message });
    }
  };

  return (
    <Layout>
      <section className="min-h-[80vh] flex items-center justify-center bg-slate-50 py-16">
        <div className="w-full max-w-lg px-4">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold text-navy mb-2">
              Join BevRelay
            </h1>
            <p className="text-slate-600">
              {step === 1 ? 'Select your role to get started' : 'Complete your profile'}
            </p>
          </div>

          {step === 1 ? (
            <div className="space-y-4" data-testid="role-selection">
              {ROLES.map((role) => (
                <button
                  key={role.value}
                  onClick={() => handleRoleSelect(role.value)}
                  className="w-full bg-white rounded-2xl border border-slate-200 p-6 shadow-sm hover:border-copper hover:shadow-md transition-all text-left group"
                  data-testid={`role-${role.value.toLowerCase()}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-navy text-lg group-hover:text-copper transition-colors">
                        {role.label}
                      </h3>
                      <p className="text-slate-600 text-sm mt-1">{role.desc}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-copper transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm"
              data-testid="register-form"
            >
              <div className="flex items-center gap-2 mb-6 text-sm text-copper">
                <CheckCircle className="w-4 h-4" />
                <span>Registering as <strong>{formData.role}</strong></span>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="ml-auto text-slate-500 hover:text-copper"
                >
                  Change
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="input-field pl-11"
                        placeholder="Your name"
                        data-testid="register-name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="input-field pl-11"
                        placeholder="you@email.com"
                        data-testid="register-email"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        minLength={6}
                        className="input-field pl-11"
                        placeholder="Min 6 characters"
                        data-testid="register-password"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        required
                        className="input-field pl-11"
                        placeholder="Confirm password"
                        data-testid="register-confirm-password"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">
                      Company {formData.role === 'Rep' ? '' : <span className="text-red-500">*</span>}
                    </label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        required={formData.role !== 'Rep'}
                        className="input-field pl-11"
                        placeholder={formData.role === 'Rep' ? 'Distributor name' : 'Company name'}
                        data-testid="register-company"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">
                      State
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className="input-field pl-11"
                        placeholder="e.g., HI, NV, IL"
                        data-testid="register-state"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Phone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="input-field pl-11"
                      placeholder="(555) 123-4567"
                      data-testid="register-phone"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={status.state === 'loading'}
                  className="w-full btn-copper justify-center disabled:opacity-60 mt-2"
                  data-testid="register-submit"
                >
                  {status.state === 'loading' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </button>

                {status.state === 'error' && (
                  <div className="rounded-xl px-4 py-3 bg-red-50 text-red-800 border border-red-200 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{status.msg}</span>
                  </div>
                )}
              </div>
            </form>
          )}

          <p className="text-center mt-6 text-slate-600">
            Already have an account?{' '}
            <Link to="/login" className="text-copper font-medium hover:text-copper-dark">
              Sign in
            </Link>
          </p>
        </div>
      </section>
    </Layout>
  );
}
